import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatedCheckmark } from '../common/AnimatedCheckmark'
import { SkeletonCard, SkeletonComment, SkeletonTimeline } from '../common/Skeleton'
import {
  useNegotiationDetail,
  clearNegotiationContext,
} from '../../hooks/useNegotiations'
import { readNegotiationContext, writeNegotiationContext } from '../../hooks/useCustomXmlParts'
import { downloadVersion, listNegotiations } from '../../services/negotiationsApi'
import { markActionCompleted } from '../common/ContextualTip'
import { SendNegotiationCreate } from './SendNegotiationCreate'
import { SendNegotiationSendback } from './SendNegotiationSendback'
import { NegotiationPickList } from './NegotiationPickList'
import { VersionConflictDialog } from './VersionConflictDialog'
import type { Actor } from '../../services/actorsApi'

interface SendNegotiationProps {
  actorId?: string
  actors?: Actor[]
  onActorChange?: (actorId: string) => void
  showToast?: (type: 'success' | 'error' | 'info', message: string) => void
  onNegotiationActive?: (active: boolean) => void
  onSwitchToSigning?: () => void
}

type ViewState = 'loading' | 'create' | 'pick' | 'sendback' | 'sending' | 'success' | 'conflict'

/** Get document filename from Office (without extension) */
async function getDocumentFilename(): Promise<string | null> {
  try {
    return await new Promise<string | null>((resolve) => {
      Office.context.document.getFilePropertiesAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded && result.value.url) {
          const url = result.value.url
          const filename = url.split(/[/\\]/).pop() ?? ''
          return resolve(filename.replace(/\.docx$/i, '').trim() || null)
        }
        resolve(null)
      })
    })
  } catch {
    return null
  }
}

export function SendNegotiation({ actorId, actors, onActorChange, showToast, onNegotiationActive, onSwitchToSigning }: SendNegotiationProps) {
  const { t } = useTranslation()

  // --- State machine ---
  const [viewState, setViewState] = useState<ViewState>('loading')
  const [negotiationId, setNegotiationId] = useState<number | undefined>()
  const [title, setTitle] = useState('')
  const [expectedVersion, setExpectedVersion] = useState(0)
  const [pickList, setPickList] = useState<Awaited<ReturnType<typeof listNegotiations>>>([])

  // Conflict state
  const [conflictVersion, setConflictVersion] = useState(0)
  const [conflictSentBy, setConflictSentBy] = useState<string | undefined>()
  const [conflictCreatedAt, setConflictCreatedAt] = useState<string | undefined>()
  const [isDownloading, setIsDownloading] = useState(false)

  // Transient UI state
  const [statusMessage, setStatusMessage] = useState('')
  const [successVersion, setSuccessVersion] = useState(0)

  // Fetch negotiation detail when we have an ID
  const { data: activeNegotiation } = useNegotiationDetail(negotiationId, actorId)

  // --- Active negotiation detection ---
  const autoSwitchedRef = useRef(false)
  const actorsRef = useRef(actors)
  actorsRef.current = actors
  const onActorChangeRef = useRef(onActorChange)
  onActorChangeRef.current = onActorChange
  const showToastRef = useRef(showToast)
  showToastRef.current = showToast
  const tRef = useRef(t)
  tRef.current = t

  useEffect(() => {
    let cancelled = false
    autoSwitchedRef.current = false

    setViewState('loading')
    setNegotiationId(undefined)
    setPickList([])

    async function detect() {
      try {
        // Try XML Part first (fast, local)
        const ctx = await readNegotiationContext()
        if (cancelled) return

        if (ctx) {
          // Auto-switch actor if negotiation belongs to a different one
          if (ctx.owner_actor_id && ctx.owner_actor_id !== actorId && onActorChangeRef.current) {
            const ownerActor = actorsRef.current?.find((a) => a.koblink_id === ctx.owner_actor_id)
            if (ownerActor) {
              autoSwitchedRef.current = true
              onActorChangeRef.current(ctx.owner_actor_id)
              showToastRef.current?.('info', tRef.current('negotiate.autoSwitchedActor', { name: ownerActor.display_name }))
              return
            }
          }

          setNegotiationId(ctx.negotiation_id)
          setExpectedVersion(ctx.current_version)
          setTitle(ctx.title)
          setViewState('sendback')
          return
        }

        // Fallback: ask backend by document filename, then by all active
        const filename = await getDocumentFilename()
        if (cancelled) return

        let matches: Awaited<ReturnType<typeof listNegotiations>> = []

        if (filename) {
          matches = await listNegotiations({ status: 'ACTIVE', actorId, documentTitle: filename })
          if (cancelled) return
        }

        if (matches.length === 0) {
          // No negotiation linked to this document — show create view.
          // Don't fall back to all active negotiations as that would show
          // unrelated negotiations from other documents.
          setViewState('create')
          return
        }

        if (matches.length > 0) {
          const neg = matches[0]
          setNegotiationId(neg.id)
          setExpectedVersion(neg.current_version)
          setTitle(neg.title)
          setViewState('sendback')
          writeNegotiationContext({
            negotiation_id: neg.id,
            current_version: neg.current_version,
            title: neg.title,
            owner_actor_id: neg.owner?.koblink_id,
          }).catch(() => {})
        } else {
          setViewState('create')
        }
      } catch {
        if (!cancelled) setViewState('create')
      }
    }

    detect()
    return () => { cancelled = true }
  }, [actorId])

  // If negotiation is no longer active, revert to create mode
  useEffect(() => {
    if (activeNegotiation && activeNegotiation.status !== 'ACTIVE' && viewState === 'sendback') {
      clearNegotiationContext()
      setNegotiationId(undefined)
      setViewState('create')
    }
  }, [activeNegotiation, viewState])

  // Notify parent of active negotiation state
  useEffect(() => {
    onNegotiationActive?.(viewState === 'sendback' || viewState === 'pick')
  }, [viewState, onNegotiationActive])

  // Auto-navigate from success to sendback after 1.5s
  useEffect(() => {
    if (viewState !== 'success' || negotiationId == null) return
    const timer = setTimeout(() => setViewState('sendback'), 1500)
    return () => clearTimeout(timer)
  }, [viewState, negotiationId])

  // --- Conflict handlers ---
  const handleConflictDownload = async () => {
    if (!negotiationId) return
    setIsDownloading(true)
    try {
      const blob = await downloadVersion(negotiationId, conflictVersion)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${title || 'document'}_v${conflictVersion}.docx`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      // Download failed — user can retry
    } finally {
      setIsDownloading(false)
    }
  }

  const handleConflictDismiss = () => {
    setExpectedVersion(conflictVersion)
    setViewState('sendback')
  }

  // --- Render: state machine dispatch ---

  if (viewState === 'loading') {
    return (
      <div className="flex-1 flex flex-col overflow-hidden animate-fade-in">
        <div className="px-3 py-3 space-y-3">
          <SkeletonCard />
        </div>
        <SkeletonTimeline />
        <div className="flex-1 py-2 space-y-1">
          <SkeletonComment />
          <SkeletonComment />
        </div>
      </div>
    )
  }

  if (viewState === 'sending') {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6">
        <div className="animate-spin h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full" />
        <p className="text-xs text-neutral-500">{statusMessage}</p>
      </div>
    )
  }

  if (viewState === 'success') {
    markActionCompleted('first-negotiation')
    const isSendBack = successVersion > 1
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6 animate-fade-in">
        <AnimatedCheckmark size={48} className="mb-2" />
        <div>
          <h3 className="text-[15px] font-bold tracking-tight text-neutral-900 dark:text-neutral-100">
            {isSendBack ? t('negotiate.versionSent') : t('negotiate.negotiationSent')}
          </h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 max-w-[220px]">
            {isSendBack
              ? t('negotiate.versionSentDescription', { version: successVersion })
              : t('negotiate.negotiationSentDescription')}
          </p>
        </div>
        <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-2 animate-pulse">
          {t('negotiate.redirectingToComments')}
        </p>
      </div>
    )
  }

  if (viewState === 'conflict') {
    return (
      <VersionConflictDialog
        serverVersion={conflictVersion}
        sentBy={conflictSentBy}
        createdAt={conflictCreatedAt}
        onDownload={handleConflictDownload}
        onDismiss={handleConflictDismiss}
        isDownloading={isDownloading}
      />
    )
  }

  if (viewState === 'pick') {
    return (
      <NegotiationPickList
        pickList={pickList}
        onSelect={(id, version, negTitle) => {
          setNegotiationId(id)
          setExpectedVersion(version)
          setTitle(negTitle)
          setViewState('sendback')
        }}
      />
    )
  }

  if (viewState === 'sendback' && activeNegotiation && negotiationId != null) {
    return (
      <SendNegotiationSendback
        negotiationId={negotiationId}
        activeNegotiation={activeNegotiation}
        expectedVersion={expectedVersion}
        title={title}
        actorId={actorId}
        showToast={showToast}
        onVersionSent={(newVersion) => {
          setExpectedVersion(newVersion)
          setSuccessVersion(newVersion)
          setViewState('success')
        }}
        onConflict={(serverVersion, sentBy, createdAt) => {
          setConflictVersion(serverVersion)
          setConflictSentBy(sentBy)
          setConflictCreatedAt(createdAt)
          setViewState('conflict')
        }}
        onSending={(msg) => {
          setStatusMessage(msg)
          setViewState('sending')
        }}
        onCancelled={() => {
          showToast?.('info', t('negotiate.negotiationCancelled'))
        }}
        onSwitchToSigning={onSwitchToSigning}
        onNegotiationActive={onNegotiationActive}
      />
    )
  }

  // Create mode (default / fallback)
  return (
    <SendNegotiationCreate
      actorId={actorId}
      title={title}
      onTitleChange={setTitle}
      showToast={showToast}
      onCreateSuccess={(id, version) => {
        setNegotiationId(id)
        setExpectedVersion(version)
        setSuccessVersion(version)
        setViewState('success')
        // Bind negotiation to this document via Custom XML Part
        writeNegotiationContext({
          negotiation_id: id,
          current_version: version,
          title: title,
          owner_actor_id: actorId,
        }).catch(() => {})
      }}
      onSending={(msg) => {
        setStatusMessage(msg)
        setViewState('sending')
      }}
      onError={() => {
        setViewState('create')
      }}
    />
  )
}
